import React from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../constants/colors'; // adjust the path as needed
import { showPrices } from '../constants/settings';

function formatPrice(amount, currency) {
  const value = amount / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

export default function ProductRow({
  product,
  coverImage,
  isLoaded,
  onImageLoad
}) {
  return (
    <div className="product-card">
      <Link to={`/product/${product.id}`} className="product-link">
        <div className="product-image-container">
          {coverImage?.imageURL ? (
            <img
              src={coverImage.imageURL}
              alt={product.productName}
              className={`fade-img product-thumbnail ${
                isLoaded ? 'loaded' : 'loading'
              }`}
              onLoad={() => onImageLoad(product.id)}
            />
          ) : (
            <div className="product-placeholder">
              <span className="material-icons placeholder-icon">photo</span>
            </div>
          )}
        </div>
        <div className="product-info">
          <div className="product-text">
            <span className="product-name">{product.productName}</span>
            {product.description && product.description.trim() !== '' && (
              <span className="product-description">{product.description}</span>
            )}
          </div>
          {showPrices &&
           typeof product.amount === 'number' &&
           product.amount > 0 &&
           product.currency ? (
            <span className="product-price">
              {formatPrice(product.amount, product.currency)}
            </span>
          ) : null}
        </div>
      </Link>

      <style jsx>{`
        .product-card {
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          margin: 16px 0;       /* vertical spacing */
          padding: 0;           /* no internal padding */
          overflow: hidden;     /* clip children to 8px radius */
          transition: background-color 0.2s ease;
        }

        .product-card:hover {
          background-color: var(--color-card-background-hover);
        }

        /* Center-align to vertically center the content */
        .product-link {
          display: flex;
          align-items: center; /* changed from flex-start to center */
          text-decoration: none;
          color: inherit;
          width: 100%;
        }

        /* No extra bottom margin/padding here */
        .product-image-container {
          margin: 0;
          padding: 0;
          flex-shrink: 0;
        }

        /* 75×75px image with no border */
        .fade-img {
          width: 75px;
          height: 75px;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
          display: block;  /* avoid inline whitespace */
          /* no border and no border-radius—parent will clip corners */
        }
        .fade-img.loaded {
          opacity: 1;
        }
        .fade-img.loading {
          opacity: 0;
        }

        /* Placeholder box—also without a border */
        .product-placeholder {
          width: 75px; /* matched to image size */
          height: 75px; /* matched to image size */
          background-color: var(--color-border);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .product-placeholder .placeholder-icon {
          font-size: 20px;
          opacity: 0.5;
          line-height: 1;
        }

        /* Only this section (name + price) has padding */
        .product-info {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
          padding: 8px 12px;   /* keep text block padded */
        }

        .product-text {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          flex: 1;
          min-width: 0;
          margin-right: 12px;
        }

        .product-name {
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-align: left;
          font-weight: bold;
        }

        .product-description {
          margin-top: 4px;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          text-align: left;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .product-price {
          flex-shrink: 0;
          font-weight: 500;
          color: ${COLORS.primary};
        }

        @media (max-width: 600px) {
          .fade-img,
          .product-placeholder {
            width: 75px;
            height: 75px;
          }
          .product-placeholder .placeholder-icon {
            font-size: 18px;
          }
          .product-image-container {
            margin-right: 4px;
          }
          .product-info {
            padding: 8px 8px;
          }
          .product-text {
            margin-right: 8px;
          }
        }
      `}</style>
    </div>
  );
}